import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import { Card, Grid, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import CustomButton from "../../components/CustomButton";
import CustomCheckbox from "../../components/CustomCheckbox";
import CustomModal from "../../components/CustomModal/CustomModal";
import CustomTextField from "../../components/CustomTextField";
import {
  getUserDetailsById,
  updateClinicUserById,
} from "../../redux/reducers/clinicSlice";
import { radioPaymentTypes } from "../../util/arrayList";
import { getBorderColor } from "../../util/function";
import PaymentModeDropdown from "./PaymentModeDropdown";

const initValues = {
  userName: "",
  userMobile: "",
  email: "",
  address: "",
  secondaryMobile: "",
  paymentType: radioPaymentTypes[0]?.value,
  advanceAmount: "",
  paymentMode: { name: "Cash", value: "cash" },
  userId: null,
  advanceBalance: 0,
};

const initErrors = {
  advanceAmount: "",
  paymentMode: "",
};

const CommonAdvancePayModalComponent = ({ open, onClose, userDetails }) => {
  const dispatch = useDispatch();
  const [userValues, setUserValues] = useState(initValues);
  const [usrErrs, setUsrErrs] = useState(initErrors);

  useEffect(() => {
    if (userDetails?.userId) {
      dispatch(getUserDetailsById(userDetails?.userId)).then((res) => {
        if (res?.payload) {
          const data = res?.payload;
          setUserValues({
            ...initValues,
            ...userDetails,
            advanceBalance: data?.advanceAmount || 0,
          });
        }
      });
    }
  }, [userDetails]);

  const handleValueChange = (value, name) => {
    const reqValue = typeof value === "object" ? value?.value : value;
    const valObj = {
      ...userValues,
      [name]: typeof value === "object" ? value : reqValue,
    };
    if (name === "paymentType") {
      valObj.paymentMode = null;
    }
    setUserValues(valObj);
    if (name === "advanceAmount") {
      const amount = Number(reqValue);
      const newErrors = { ...usrErrs };

      if (isNaN(amount)) {
        newErrors.advanceAmount = "Please enter a valid number";
      } else if (amount <= 0) {
        newErrors.advanceAmount = "Amount must be greater than 0";
      } else if (
        userValues.paymentType === "Refund" &&
        amount > userValues.advanceBalance
      ) {
        newErrors.advanceAmount = "Refund amount exceeds available balance";
      } else {
        newErrors.advanceAmount = "";
      }

      setUsrErrs(newErrors);
    }
  };

  const validateForm = () => {
    const errors = {};
    let isValid = true;

    if (!userValues.paymentMode) {
      errors.paymentMode = "Please select a payment mode";
      isValid = false;
    }

    if (!userValues.advanceAmount || Number(userValues.advanceAmount) <= 0) {
      errors.advanceAmount = "Please enter a valid amount";
      isValid = false;
    }

    setUsrErrs(errors);
    return isValid;
  };

  const handlePay = () => {
    if (!validateForm()) return;

    const form = new FormData();
    Object.entries(userValues).forEach(([key, value]) => {
      if (
        value !== null &&
        value !== undefined &&
        !["paymentMode", "advanceAmount"].includes(key)
      ) {
        form.append(key, value);
      }
    });

    if (userValues.paymentMode) {
      form.append("paymentMode", userValues.paymentMode.value);
    }

    form.append(
      "advanceAmount",
      Number(
        `${userValues.paymentType === "Collect" ? "+" : "-"}${
          userValues.advanceAmount
        }`
      )
    );

    dispatch(
      updateClinicUserById({
        userId: userValues.userId,
        form,
        successMsg: "Payment updated successfully!",
      })
    ).then((res) => {
      if (res?.payload) {
        dispatch(getUserDetailsById(userDetails?.userId)).then((res) => {
          if (res?.payload) {
            const data = res?.payload;
            setUserValues((prev) => ({
              ...prev,
              advanceAmount: data?.advanceAmount || 0,
              advanceBalance: data?.advanceAmount || 0,
            }));
          }
        });
        onClose("api");
      }
    });
  };

  return (
    <CustomModal
      open={open}
      onClose={onClose}
      header="Pay"
      rightModal
      modalWidth={30}
    >
      <div className="flex-center">
        <Card
          sx={{
            borderRadius: 1,
            padding: 2,
            borderTopColor: `${getBorderColor("paid")}`,
          }}
          className="pay-card"
        >
          <div className="maint">
            <div>
              <div className="flex-row parentcontainer">
                <div className="flex-row iconcontainer">
                  <AccountCircleOutlinedIcon sx={{ width: 25, height: 25 }} />
                  <Typography className="font-bold fs14 capitalize flex-center h35">
                    {userValues?.userName}
                  </Typography>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>
      <Grid container spacing={2} className="ph20">
        <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
          <div className="flex-row-ali-cen">
            <Typography className="font-medium fs14 green-color mt15">
              Available Balance:{" "}
              {Number(userValues?.advanceBalance)
                ? `Rs ${Number(userValues?.advanceBalance).toFixed(2)}`
                : "Nil"}
            </Typography>
          </div>
        </Grid>
        <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
          <div className="flex-row-ali-cen">
            <Typography className="font-medium fs14 black mb5">
              Credit Balance:
            </Typography>
            <div className="ml10">
              <CustomCheckbox
                radio
                value={userValues?.paymentType}
                onChange={(e) =>
                  handleValueChange(e?.target?.value, "paymentType")
                }
                radios={radioPaymentTypes}
                defaultValue={userValues?.paymentType}
              />
            </div>
          </div>
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
          <div className="mb20">
            <CustomTextField
              label="Amount"
              name="advanceAmount"
              fullWidth
              handleChange={(e) =>
                handleValueChange(e?.target?.value, "advanceAmount")
              }
              value={userValues?.advanceAmount}
              error={!!usrErrs?.advanceAmount}
              helperText={usrErrs?.advanceAmount}
              type="number"
            />
          </div>
        </Grid>
        <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
          {/* <div className="font-medium fs14 black2">Payment Mode</div> */}
          <div className="mb20">
            <PaymentModeDropdown
              value={userValues?.paymentMode}
              onChange={(value) => handleValueChange(value, "paymentMode")}
              name="paymentMode"
              error={!!usrErrs?.paymentMode}
              helperText={usrErrs?.paymentMode}
              hideAdvancedAdjustment={
                userValues?.paymentType?.toLocaleLowerCase() === "collect"
              }
            />
          </div>
        </Grid>
        <div className="flex1-end mt20">
          <div>
            <CustomButton text="Pay" onClick={handlePay} smallBtn />
          </div>
        </div>
      </Grid>
    </CustomModal>
  );
};

export default CommonAdvancePayModalComponent;
